import moment from "moment-timezone";
import FeaturesService from "./featuresService";

/**
 * DateFormatService
 *
 * Centralized service for date formatting with configurable formats per facility.
 *
 * Fallback hierarchy:
 * 1. Facility-specific format (from defaultDateFormatForFacility config)
 * 2. Browser locale/region format
 * 3. UK/Ireland default format (DD/MM/YYYY)
 *
 * Uses native JavaScript where possible, moment.js only when needed.
 */
class DateFormatService {
  constructor() {
    // Cache for detected locale format
    this._cachedLocaleFormat = null;
    this._cachedTimeFormat = null;
    // Timezone handling: facility -> browser -> Ireland default
    this._facilityTimezone = null; // Will be set from backend config (e.g., "Europe/Dublin")
    this._browserTimezone = moment.tz.guess(); // Auto-detect browser timezone
    this._defaultTimezone = "Europe/Dublin"; // Ireland fallback with DST
  }

  /**
   * Get the timezone to use for date display
   * Priority: Facility timezone > Browser timezone > Ireland default (with DST)
   * @returns {string} Timezone identifier (e.g., "Europe/Dublin", "America/New_York")
   */
  getTimezone() {
    // Level 1: Facility timezone (from backend config)
    if (this._facilityTimezone) {
      return this._facilityTimezone;
    }

    // Level 2: Browser timezone (auto-detected)
    if (this._browserTimezone) {
      return this._browserTimezone;
    }

    // Level 3: Ireland default with DST
    return this._defaultTimezone;
  }

  /**
   * Set facility timezone (called when backend provides timezone config)
   * @param {string} timezone - IANA timezone identifier (e.g., "Europe/Dublin")
   */
  setFacilityTimezone(timezone) {
    this._facilityTimezone = timezone;
  }

  /**
   * Get the date format string to use
   * Priority: Facility config > Browser locale > UK/Ireland default
   * @returns {string} Date format string (e.g., "DD/MM/YYYY", "MM/DD/YYYY")
   */
  getDateFormat() {
    // Level 1: Check facility configuration (placeholder for future backend integration)
    const facilityFormat = this._getFacilityDateFormat();
    if (facilityFormat) {
      return facilityFormat;
    }

    // Level 2: Try to detect from browser locale
    const localeFormat = this._detectLocaleFormat();
    if (localeFormat) {
      return localeFormat;
    }

    // Level 3: Fallback to UK/Ireland format
    return "DD/MM/YYYY";
  }

  /**
   * Get the time format string to use (12h vs 24h)
   * @param {boolean} includeSeconds - Whether to include seconds
   * @returns {string} Time format string (e.g., "HH:mm", "h:mm A")
   */
  getTimeFormat(includeSeconds = false) {
    const use24Hour = this._shouldUse24HourTime();

    if (use24Hour) {
      return includeSeconds ? "HH:mm:ss" : "HH:mm";
    } else {
      return includeSeconds ? "h:mm:ss A" : "h:mm A";
    }
  }

  /**
   * Get combined date and time format
   * @param {boolean} includeSeconds - Whether to include seconds
   * @returns {string} DateTime format string
   */
  getDateTimeFormat(includeSeconds = false) {
    return `${this.getDateFormat()} ${this.getTimeFormat(includeSeconds)}`;
  }

  /**
   * Format a date for display (date only, no time)
   * Accepts multiple input formats:
   * - ISO strings (2024-03-15T10:30:00Z)
   * - Unix timestamps (1710497400000)
   * - Date objects
   * - Common formats (03/15/2024, 15-03-2024, DD/MM/YYYY)
   * @param {Date|string|number} date - Date to format
   * @returns {string} Formatted date string
   */
  formatDate(date) {
    if (!date) {
      return "";
    }

    try {
      const parsedDate = this._parseFlexibleDate(date);
      if (!parsedDate) {
        // Fallback to native browser formatting
        return this._nativeDateFormat(date);
      }

      // Apply timezone (handles DST automatically)
      const timezone = this.getTimezone();
      const zonedDate = parsedDate.tz(timezone);

      const format = this.getDateFormat();
      return zonedDate.format(format);
    } catch (e) {
      // Ultimate fallback to native browser formatting
      console.warn("[DateFormatService] formatDate fallback to native:", e);
      return this._nativeDateFormat(date);
    }
  }

  /**
   * Format a date showing only month and year (no day)
   * Used for DOB when useOnlyMonthYearForDOB feature is enabled
   * @param {Date|string|number} date - Date to format
   * @returns {string} Formatted month/year string (e.g., "03/2024" or "2024-03")
   */
  formatDateMonthYear(date) {
    if (!date) {
      return "";
    }

    const parsedDate = this._parseFlexibleDate(date);
    if (!parsedDate) {
      return "";
    }

    // Apply timezone (handles DST automatically)
    const timezone = this.getTimezone();
    const zonedDate = parsedDate.tz(timezone);

    // Get base format and derive month/year format from it
    const baseFormat = this.getDateFormat();
    const monthYearFormat = this._getMonthYearFormat(baseFormat);
    return zonedDate.format(monthYearFormat);
  }

  /**
   * Format a Date of Birth (DOB) for display
   * DOB dates are stored without time and must be parsed as UTC to avoid timezone shifts
   * Never includes time component
   * @param {Date|string|number} date - DOB date to format
   * @returns {string} Formatted date string
   */
  formatDOB(date) {
    if (date == null) {
      return "";
    }

    // Parse DOB as UTC to avoid timezone shifting issues
    // Birth dates have no time component and are stored as UTC midnight
    const parsedDate = moment.tz(date, "UTC");
    if (!parsedDate.isValid()) {
      return "";
    }

    // Format using locale-aware date format (no timezone conversion for DOB display)
    const format = this.getDateFormat();
    return parsedDate.format(format);
  }

  /**
   * Format a Date of Birth showing only month and year
   * DOB dates are stored without time and must be parsed as UTC
   * @param {Date|string|number} date - DOB date to format
   * @returns {string} Formatted month/year string
   */
  formatDOBMonthYear(date) {
    if (date == null) {
      return "";
    }

    // Parse DOB as UTC to avoid timezone shifting issues
    const parsedDate = moment.tz(date, "UTC");
    if (!parsedDate.isValid()) {
      return "";
    }

    // Get base format and derive month/year format from it
    const baseFormat = this.getDateFormat();
    const monthYearFormat = this._getMonthYearFormat(baseFormat);
    return parsedDate.format(monthYearFormat);
  }

  /**
   * Derive month/year format from full date format
   * @private
   * @param {string} dateFormat - Full date format (e.g., "DD/MM/YYYY")
   * @returns {string} Month/year format (e.g., "MM/YYYY")
   */
  _getMonthYearFormat(dateFormat) {
    // Remove day component from various formats
    const formatMappings = {
      "DD/MM/YYYY": "MM/YYYY",
      "MM/DD/YYYY": "MM/YYYY",
      "YYYY-MM-DD": "YYYY-MM",
      "DD-MM-YYYY": "MM-YYYY",
      "DD.MM.YYYY": "MM.YYYY",
    };

    return formatMappings[dateFormat] || "MM/YYYY";
  }

  /**
   * Format a date with time for display
   * @param {Date|string|number} date - Date to format
   * @param {boolean} includeSeconds - Whether to include seconds
   * @returns {string} Formatted date-time string
   */
  formatDateTime(date, includeSeconds = false) {
    if (!date) {
      return "";
    }

    try {
      const parsedDate = this._parseFlexibleDate(date);
      if (!parsedDate) {
        // Fallback to native browser formatting
        return this._nativeDateTimeFormat(date);
      }

      // Apply timezone (handles DST automatically)
      const timezone = this.getTimezone();
      const zonedDate = parsedDate.tz(timezone);

      const format = this.getDateTimeFormat(includeSeconds);
      return zonedDate.format(format);
    } catch (e) {
      // Ultimate fallback to native browser formatting
      console.warn("[DateFormatService] formatDateTime fallback to native:", e);
      return this._nativeDateTimeFormat(date);
    }
  }

  /**
   * Format time only (no date)
   * @param {Date|string|number} date - Date/time to format
   * @param {boolean} includeSeconds - Whether to include seconds
   * @returns {string} Formatted time string
   */
  formatTime(date, includeSeconds = false) {
    if (!date) {
      return "";
    }

    const parsedDate = this._parseFlexibleDate(date);
    if (!parsedDate) {
      return "";
    }

    // Apply timezone (handles DST automatically)
    const timezone = this.getTimezone();
    const zonedDate = parsedDate.tz(timezone);

    const format = this.getTimeFormat(includeSeconds);
    return zonedDate.format(format);
  }

  /**
   * Format a date that might be stored as a string attribute
   * Handles both ISO strings and epoch timestamps
   * Uses flexible parsing to handle various input formats
   * @param {string|number|Date} value - Date value (could be string, timestamp, or Date)
   * @returns {string} Formatted date string
   */
  formatAttributeDateValue(value) {
    if (!value) {
      return "";
    }

    const parsedDate = this._parseFlexibleDate(value);
    if (!parsedDate) {
      // If we can't parse it and it's a string, return as-is
      // This handles edge cases where the value is already formatted text
      return typeof value === "string" ? value : "";
    }

    // Apply timezone (handles DST automatically)
    const timezone = this.getTimezone();
    const zonedDate = parsedDate.tz(timezone);

    const format = this.getDateFormat();
    return zonedDate.format(format);
  }

  /**
   * Format a date-time that might be stored as a string attribute
   * Uses flexible parsing to handle various input formats
   * @param {string|number|Date} value - DateTime value
   * @param {boolean} includeSeconds - Whether to include seconds
   * @returns {string} Formatted date-time string
   */
  formatAttributeDateTimeValue(value, includeSeconds = false) {
    if (!value) {
      return "";
    }

    const parsedDate = this._parseFlexibleDate(value);
    if (!parsedDate) {
      // If we can't parse it and it's a string, return as-is
      return typeof value === "string" ? value : "";
    }

    // Apply timezone (handles DST automatically)
    const timezone = this.getTimezone();
    const zonedDate = parsedDate.tz(timezone);

    const format = this.getDateTimeFormat(includeSeconds);
    return zonedDate.format(format);
  }

  /**
   * Parse a date string in the current format back to a Date object
   * @param {string} dateString - Date string in current format
   * @returns {Date|null} Parsed Date object or null if invalid
   */
  parseDate(dateString) {
    if (!dateString) {
      return null;
    }

    const format = this.getDateFormat();
    const parsed = moment(dateString, format, true);

    if (parsed.isValid()) {
      return parsed.toDate();
    }

    return null;
  }

  /**
   * Get the format string for input fields (HTML5 date input uses YYYY-MM-DD)
   * @returns {string} Format string for input fields
   */
  getInputFormat() {
    return "YYYY-MM-DD";
  }

  /**
   * Convert a date to the format needed for input fields
   * @param {Date|string|number} date - Date to convert
   * @returns {string} Date string in YYYY-MM-DD format
   */
  formatForInput(date) {
    if (!date) {
      return "";
    }
    return moment(date).format(this.getInputFormat());
  }

  /**
   * Check if an attribute is a Date of Birth (DOB) attribute
   * Checks both attribute name, dataField, and observation code
   * @param {object} attribute - Attribute object to check
   * @returns {boolean} True if this is a DOB attribute
   */
  isDOBAttribute(attribute) {
    if (!attribute) {
      return false;
    }

    const dobNames = [
      "MPS_DOB",
      "PatientDOB",
      "DateOfBirth",
      "DOB",
      "mps_dob",
      "DateofBirth",
      "DATE_OF_BIRTH",
      "PATIENT_DOB",
      "Patient_DOB",
      "patient_dob",
      "MPS_PATIENT_DATEOFBIRTH"
    ];

    // Check by attribute name
    const isDOBByName = attribute.name && dobNames.some(name =>
      attribute.name.toLowerCase() === name.toLowerCase()
    );

    // Check by dataField (more reliable according to git history)
    const isDOBByDataField = attribute.dataField === "PatientDOB";

    // Check by observation code (for OBSERVATION_DATE types)
    const isDOBByObservationCode =
      attribute.valueObservation?.code?.code &&
      dobNames.some(name =>
        attribute.valueObservation.code.code.toLowerCase() === name.toLowerCase()
      );

    return isDOBByName || isDOBByDataField || isDOBByObservationCode;
  }

  /**
   * Format a string observation value that may contain a date/datetime
   * Handles specific known observations that store dates as strings
   * @param {string} value - String value that may contain a date
   * @param {object} observationCode - The observation code object
   * @returns {string} Formatted date-time string or original value
   */
  formatStringObservationIfDate(value, observationCode) {
    if (!value || typeof value !== "string") {
      return value || "";
    }

    // Hardcoded check for specific observations known to contain dates
    const dateStringObservations = [
      "CORSANO_LAST_USED_DATE"
    ];

    const isDateObservation = observationCode?.code &&
      dateStringObservations.some(name =>
        observationCode.code.toLowerCase() === name.toLowerCase()
      );

    if (isDateObservation) {
      // This observation stores dates as strings like "2025-11-10 12:15:08"
      return this.formatDateTime(value);
    }

    // Not a known date observation, return as-is
    return value;
  }

  /**
   * Check if a value looks like a formatted date/datetime
   * Used for applying CSS styling to date values
   * @param {string} value - The value to check
   * @returns {boolean} True if value appears to be a formatted date
   */
  isFormattedDate(value) {
    if (!value || typeof value !== "string") {
      return false;
    }

    // Pattern 1: DD/MM/YYYY or MM/DD/YYYY (with optional time)
    // Examples: "10/10/2025", "10/10/2025 13:43", "10/10/2025 08:41"
    const datePattern1 = /^\d{1,2}\/\d{1,2}\/\d{4}(\s+\d{1,2}:\d{2}(:\d{2})?)?$/;

    // Pattern 2: DD-MM-YYYY or YYYY-MM-DD (with optional time)
    // Examples: "10-10-2025", "2025-10-10", "2025-10-10 13:43"
    const datePattern2 = /^\d{1,4}-\d{1,2}-\d{1,4}(\s+\d{1,2}:\d{2}(:\d{2})?)?$/;

    // Pattern 3: DD.MM.YYYY (with optional time)
    // Examples: "10.10.2025", "10.10.2025 13:43"
    const datePattern3 = /^\d{1,2}\.\d{1,2}\.\d{4}(\s+\d{1,2}:\d{2}(:\d{2})?)?$/;

    // Pattern 4: DD MMM YYYY or DD MMMM YYYY (with optional time)
    // Examples: "10 Oct 2025", "10 October 2025", "03 Jul 1977"
    const datePattern4 = /^\d{1,2}\s+[A-Za-z]{3,}\s+\d{4}(\s+\d{1,2}:\d{2}(:\d{2})?)?$/;

    return datePattern1.test(value) ||
           datePattern2.test(value) ||
           datePattern3.test(value) ||
           datePattern4.test(value);
  }

  // ========== PRIVATE METHODS ==========

  /**
   * Native browser date formatting fallback using toLocaleDateString
   * @private
   * @param {Date|string|number} date - Date to format
   * @returns {string} Formatted date string
   */
  _nativeDateFormat(date) {
    try {
      const dateObj = date instanceof Date ? date : new Date(date);
      if (isNaN(dateObj.getTime())) {
        return "";
      }
      const locale = navigator.language || "en-IE";
      return dateObj.toLocaleDateString(locale);
    } catch (e) {
      console.warn("[DateFormatService] _nativeDateFormat failed:", e);
      return "";
    }
  }

  /**
   * Native browser datetime formatting fallback using toLocaleString
   * @private
   * @param {Date|string|number} date - Date to format
   * @returns {string} Formatted datetime string
   */
  _nativeDateTimeFormat(date) {
    try {
      const dateObj = date instanceof Date ? date : new Date(date);
      if (isNaN(dateObj.getTime())) {
        return "";
      }
      const locale = navigator.language || "en-IE";
      return dateObj.toLocaleString(locale);
    } catch (e) {
      console.warn("[DateFormatService] _nativeDateTimeFormat failed:", e);
      return "";
    }
  }

  /**
   * Get facility-specific date format from configuration
   * @private
   * @returns {string|null} Facility date format or null
   */
  _getFacilityDateFormat() {
    // Placeholder for future backend integration
    // This will eventually come from space/facility details
    const facilityConfig = this._getFacilityConfig();

    if (facilityConfig && facilityConfig.defaultDateFormatForFacility) {
      return this._convertToMomentFormat(facilityConfig.defaultDateFormatForFacility);
    }

    return null;
  }

  /**
   * Get facility configuration (placeholder for backend integration)
   * @private
   * @returns {object|null} Facility config object
   */
  _getFacilityConfig() {
    // TODO: This will eventually fetch from Redux store or API
    // For now, check if FeaturesService has this capability
    if (FeaturesService && typeof FeaturesService.getFacilityConfig === "function") {
      return FeaturesService.getFacilityConfig();
    }

    return null;
  }

  /**
   * Convert various date format notations to moment.js format
   * @private
   * @param {string} format - Date format string (might be in various notations)
   * @returns {string} Moment.js format string
   */
  _convertToMomentFormat(format) {
    if (!format) {
      return null;
    }

    // If already in moment format (contains moment tokens), return as-is
    if (/[DMY]/.test(format)) {
      return format;
    }

    // Convert common notations to moment format
    const conversions = {
      "dd/mm/yyyy": "DD/MM/YYYY",
      "mm/dd/yyyy": "MM/DD/YYYY",
      "yyyy-mm-dd": "YYYY-MM-DD",
      "dd-mm-yyyy": "DD-MM-YYYY",
      "dd.mm.yyyy": "DD.MM.YYYY",
    };

    const normalized = format.toLowerCase().replace(/[^a-z\/\-\.]/g, "");
    return conversions[normalized] || format;
  }

  /**
   * Detect date format from browser locale
   * @private
   * @returns {string|null} Detected format string or null
   */
  _detectLocaleFormat() {
    if (this._cachedLocaleFormat) {
      return this._cachedLocaleFormat;
    }

    try {
      // Use Intl API to detect locale date format
      const locale = navigator.language || navigator.userLanguage || "en-GB";

      // Create a sample date to detect format
      const sampleDate = new Date(2024, 0, 15); // Jan 15, 2024
      const formatted = sampleDate.toLocaleDateString(locale);

      // Analyze the formatted string to determine pattern
      let detectedFormat = null;

      // Check if it's MM/DD or DD/MM by looking at the first number
      if (formatted.includes("/")) {
        const parts = formatted.split("/");
        if (parts.length === 3) {
          const first = parseInt(parts[0], 10);
          // If first number is 1, it's likely MM/DD (January = 01)
          // If first number is 15, it's likely DD/MM
          if (first === 1) {
            detectedFormat = "MM/DD/YYYY";
          } else if (first === 15) {
            detectedFormat = "DD/MM/YYYY";
          }
        }
      } else if (formatted.includes(".")) {
        detectedFormat = "DD.MM.YYYY";
      } else if (formatted.includes("-")) {
        // Could be YYYY-MM-DD or DD-MM-YYYY
        const parts = formatted.split("-");
        if (parts.length === 3) {
          const first = parseInt(parts[0], 10);
          if (first === 2024) {
            detectedFormat = "YYYY-MM-DD";
          } else if (first === 15) {
            detectedFormat = "DD-MM-YYYY";
          }
        }
      }

      this._cachedLocaleFormat = detectedFormat;
      return detectedFormat;
    } catch (error) {
      console.warn("[DateFormatService] Error detecting locale format:", error);
      return null;
    }
  }

  /**
   * Determine if locale prefers 24-hour time format
   * @private
   * @returns {boolean} True if 24-hour format should be used
   */
  _shouldUse24HourTime() {
    if (this._cachedTimeFormat !== null) {
      return this._cachedTimeFormat;
    }

    try {
      const locale = navigator.language || navigator.userLanguage || "en-GB";

      // Create a sample time
      const sampleTime = new Date(2024, 0, 1, 14, 30); // 2:30 PM
      const formatted = sampleTime.toLocaleTimeString(locale);

      // If the formatted string contains "PM" or "AM", it's 12-hour format
      const is12Hour = /AM|PM|am|pm/.test(formatted);
      const is24Hour = !is12Hour;

      this._cachedTimeFormat = is24Hour;
      return is24Hour;
    } catch (error) {
      console.warn("[DateFormatService] Error detecting time format:", error);
      // Default to 24-hour for UK/Ireland
      return true;
    }
  }

  /**
   * Clear cached formats (useful if locale changes)
   */
  clearCache() {
    this._cachedLocaleFormat = null;
    this._cachedTimeFormat = null;
  }

  /**
   * Parse a date from various input formats with intelligent fallback
   * Handles:
   * - ISO strings (2024-03-15T10:30:00Z, 2024-03-15T10:30:00.000Z, 2024-03-15)
   * - Unix timestamps in milliseconds (1710497400000)
   * - Unix timestamps in seconds (1710497400)
   * - Date objects
   * - Common date strings (03/15/2024, 15-03-2024, 15.03.2024)
   * - Moment objects
   * @private
   * @param {Date|string|number|moment.Moment} input - Date in any format
   * @returns {moment.Moment|null} Parsed moment object or null if invalid
   */
  _parseFlexibleDate(input) {
    if (!input) {
      return null;
    }

    // If already a moment object, return it
    if (moment.isMoment(input)) {
      return input.isValid() ? input : null;
    }

    // If it's a Date object
    if (input instanceof Date) {
      const m = moment(input);
      return m.isValid() ? m : null;
    }

    // If it's a number (timestamp)
    if (typeof input === "number") {
      // Check if it's in seconds (less than year 3000 in seconds)
      // Unix timestamps in seconds are < 10 digits, in milliseconds are 13 digits
      // IMPORTANT: Only multiply if it's a POSITIVE small number (seconds)
      // Negative timestamps (dates before 1970) are already in milliseconds
      const timestamp = (input > 0 && input < 10000000000) ? input * 1000 : input;
      const m = moment(timestamp);
      return m.isValid() ? m : null;
    }

    // If it's a string, try various parsing strategies
    if (typeof input === "string") {
      // Trim whitespace
      const trimmed = input.trim();

      // Empty string
      if (!trimmed) {
        return null;
      }

      // Strategy 1: Try ISO 8601 format (most common in backend)
      // Examples: 2024-03-15T10:30:00Z, 2024-03-15T10:30:00.000Z, 2024-03-15
      if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
        const m = moment(trimmed, moment.ISO_8601, true);
        if (m.isValid()) {
          return m;
        }
      }

      // Strategy 2: Try timestamp as string
      if (/^\d+$/.test(trimmed)) {
        const timestamp = parseInt(trimmed, 10);
        return this._parseFlexibleDate(timestamp);
      }

      // Strategy 3: Try common date formats
      const commonFormats = [
        "YYYY-MM-DD",
        "DD/MM/YYYY",
        "MM/DD/YYYY",
        "DD-MM-YYYY",
        "MM-DD-YYYY",
        "DD.MM.YYYY",
        "MM.DD.YYYY",
        "YYYY/MM/DD",
        "DD MMM YYYY",
        "MMM DD, YYYY",
        "DD MMMM YYYY",
        "MMMM DD, YYYY",
        "YYYY-MM-DD HH:mm:ss",
        "DD/MM/YYYY HH:mm:ss",
        "MM/DD/YYYY HH:mm:ss",
        "DD/MM/YYYY HH:mm",
        "MM/DD/YYYY HH:mm",
        "DD.MM.YYYY HH:mm",
        "DD MMM YYYY HH:mm",
        "YYYY-MM-DDTHH:mm:ss",
        "YYYY-MM-DDTHH:mm:ss.SSS",
      ];

      for (const format of commonFormats) {
        const m = moment(trimmed, format, true);
        if (m.isValid()) {
          return m;
        }
      }

      // Strategy 4: Try moment's lenient parsing as last resort
      // Note: This is more permissive and may parse invalid dates
      const m = moment(trimmed);
      if (m.isValid()) {
        // Additional sanity check: year should be reasonable (1900-2200)
        const year = m.year();
        if (year >= 1900 && year <= 2200) {
          return m;
        }
      }
    }

    // Could not parse
    console.warn("[DateFormatService] Could not parse date:", input);
    return null;
  }
}

// Export singleton instance
const dateFormatService = new DateFormatService();
export default dateFormatService;
